import { z } from 'zod';
import { MetaSchema, Rfc3339DateTime } from './create-chat.js';

/**
 * Shared BFF↔SPA response contracts for the Drop-6 workstream Files API
 * (ADR-0045). The BFF's artifact routes invoke the `workstreamagent`
 * (`listArtifacts` / `getArtifact` / `deleteArtifact`, `workstream-agent.ts`),
 * narrow the agent's tolerant-out reply into these shapes, and return them in
 * the standard `{ data, meta }` envelope. The SPA's query/mutation hooks parse
 * against these.
 *
 * `data` schemas are **strict** (the BFF authors them; a stray key is a bug);
 * the outer envelopes are **loose** (`z.looseObject`, ADR-0024 §11 — a future
 * additive field never breaks an old client).
 */

/**
 * One file's metadata, as listed in the Files panel. `artifactId` is the OPAQUE
 * per-artifact id (the uuid segment of the S3 key, ADR-0046) — the SPA uses it as
 * the `{artifactId}` path segment for view/delete; the durable S3 key stays
 * server-side. `lastModified` is the S3 object's timestamp — there is no
 * per-artifact date on the ledger itself. No `url`/`preview` here: the list is
 * metadata-only; a fresh presigned URL is fetched on demand by `getArtifact`.
 */
export const ArtifactListItemSchema = z.strictObject({
  artifactId: z.string().min(1).max(128),
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative(),
  lastModified: Rfc3339DateTime,
});
export type ArtifactListItem = z.infer<typeof ArtifactListItemSchema>;

/** `data` of `GET /api/v1/workstreams/{id}/artifacts`. */
export const ListArtifactsDataSchema = z.strictObject({
  artifacts: z.array(ArtifactListItemSchema),
});
export type ListArtifactsData = z.infer<typeof ListArtifactsDataSchema>;

export const ListArtifactsResponseSchema = z.looseObject({
  data: ListArtifactsDataSchema,
  meta: MetaSchema,
});
export type ListArtifactsResponse = z.infer<typeof ListArtifactsResponseSchema>;

/**
 * `data` of `GET /api/v1/workstreams/{id}/artifacts/{artifactId}` — one file
 * fetched for viewing. `content` carries the full text for text-family types
 * (rendered inline: HTML in the sandboxed iframe, markdown/code); `url` is a
 * freshly-presigned GET for download / binary. `null` from the agent normalises
 * to `undefined`.
 */
export const GetArtifactDataSchema = z.strictObject({
  artifactId: z.string().min(1).max(128),
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative(),
  content: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined),
  // A presigned S3 GET is always https; pin the scheme so a buggy/compromised
  // agent can't smuggle a `javascript:` / `data:` / `file:` URL into a value the
  // SPA navigates to (defence-in-depth — the SPA consumer also handles links safely).
  url: z
    .url({ protocol: /^https$/ })
    .nullish()
    .transform((v) => v ?? undefined),
});
export type GetArtifactData = z.infer<typeof GetArtifactDataSchema>;

export const GetArtifactResponseSchema = z.looseObject({
  data: GetArtifactDataSchema,
  meta: MetaSchema,
});
export type GetArtifactResponse = z.infer<typeof GetArtifactResponseSchema>;

/** `data` of `DELETE /api/v1/workstreams/{id}/artifacts/{artifactId}` — the deleted id (idempotent). */
export const DeleteArtifactDataSchema = z.strictObject({
  artifactId: z.string().min(1).max(128),
});
export type DeleteArtifactData = z.infer<typeof DeleteArtifactDataSchema>;

export const DeleteArtifactResponseSchema = z.looseObject({
  data: DeleteArtifactDataSchema,
  meta: MetaSchema,
});
export type DeleteArtifactResponse = z.infer<typeof DeleteArtifactResponseSchema>;
